import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { object, func, bool, string } from 'prop-types';
import cn from 'classnames';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import Placeholder from '../../Placeholder/Placeholder';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import FileViewer from 'ps-components/ReactFileViewer';
import { Toggle } from 'ps-design';
import * as marketplaceMatchesActions from '../MarketplaceMatchesActions';
import { formatCurrency } from 'ps-components/Utils/currencyUtils';

import './MarketplaceMatchesSlideout.scss';

PurchaseSlideout.propTypes = {
  surveyDetails: object,
  hideSlideout: func,
  actions: {
    getSurveyDetails: func,
    purchaseSubmit: func,
    resetPurchaseSubmit: func
  },
  onBack: func,
  isBigger: bool,
  purchaseSubmit: {
    loading: bool,
    success: bool,
    error: string
  },
  initToggle: bool
};

function PurchaseSlideout(props) {
  const [termsChecked, setTermsChecked] = useState(false);
  const [surveyTermschecked, setSurveyTermsChecked] = useState(false);
  const [surveyTermscheckedViewed, setSurveyTermsCheckedViewed] = useState(false);
  const [showParticipationPrice, setShowParticipationPrice] = useState(props.initToggle);

  let allGood = termsChecked && surveyTermschecked && surveyTermscheckedViewed;
  const payscaleTermsPDFURL = 'https://www.payscale.com/content/legal/productuseterms.pdf';

  const { surveyDetails, onBack, isBigger, purchaseSubmit } = props;
  const details = surveyDetails?.details;
  const hasBothPrices = details?.purchase_price != null && details?.participation_purchase_price != null;

  const publisher = details?.pub_survey_publisher?.toLowerCase();
  const isMercerOrPayscale = publisher?.includes('mercer') || publisher?.includes('payscale');
  if (termsChecked && isMercerOrPayscale) {
    allGood = true;
  }

  const noLegalDocsPlaceholder = (
    <div
      id="marketplace-matches__details-no-results-container"
      className="marketplace-matches__details-no-results-container"
    >
      <Placeholder primaryText="No PayScale terms and conditions found" />
    </div>
  );

  function getPayscaleTermCheck() {
    return (
      <div className="marketplace-matches__terms-row top">
        <CheckboxInput checked={termsChecked} className="terms-check" onClick={() => setTermsChecked(!termsChecked)} />

        <div className="terms-text">I have read and agree to the above terms and conditions</div>

        {hasBothPrices && (
          <div className="purchase-slideout__toggle-container">
            <Toggle
              onChange={togglePurchasePrice}
              id="purchase-slideout__toggle"
              className="purchase-slideout__toggle"
              value={showParticipationPrice}
            />
            <span className="toggle-text">Show Participation Price</span>
          </div>
        )}
      </div>
    );
  }

  function getTermsURL() {
    const url = details?.contract_language_url;

    if (url?.startsWith('www.')) {
      return `//${url}`;
    }

    return url;
  }

  function togglePurchasePrice() {
    setShowParticipationPrice(!showParticipationPrice);
  }

  function getSurveyTermCheck() {
    return (
      <div
        className={cn('marketplace-matches__terms-row bottom', {
          'more-padding': isMercerOrPayscale && !details?.purchase_price
        })}
      >
        {!isMercerOrPayscale && (
          <Fragment>
            <CheckboxInput
              checked={surveyTermschecked}
              className="terms-check"
              onClick={() => setSurveyTermsChecked(!surveyTermschecked)}
              disabled={!surveyTermscheckedViewed}
              tooltipText={!surveyTermscheckedViewed ? 'Please read survey terms and conditions' : ''}
            />

            <div className="terms-text">
              I have read and agree to
              <a
                className="terms-text-link"
                onClick={() => setSurveyTermsCheckedViewed(true)}
                href={getTermsURL()}
                target="_blank"
              >
                survey publisher terms and conditions
              </a>
            </div>
          </Fragment>
        )}

        <Fragment>
          {details?.purchase_price && !showParticipationPrice && (
            <div className="survey-price">Survey Price: {formatCurrency(details?.purchase_price, 'USD')} USD</div>
          )}

          {details?.participation_purchase_price && showParticipationPrice && (
            <div className="survey-price">
              Survey Price: {formatCurrency(details?.participation_purchase_price, 'USD')} USD
            </div>
          )}

          {details?.participation_purchase_price && !details?.purchase_price && (
            <div className="survey-price-container">
              <div className="survey-price">
                Survey Price: {formatCurrency(details?.participation_purchase_price, 'USD')} USD
              </div>
              <div className="survey-price">(Participation Required)</div>
            </div>
          )}
        </Fragment>
      </div>
    );
  }

  function getSubmitRow() {
    return (
      <div className="marketplace-matches__purchase-name-row">
        {showParticipationPrice || (details?.participation_purchase_price && !details?.purchase_price) ? (
          <div className="participation-interest-text">
            I would like to submit my interest in participating in and purchasing this survey
          </div>
        ) : (
          <div className="participation-interest-text">
            I would like to submit my interest in purchasing this survey
          </div>
        )}

        <div className="apply-button">
          {!surveyDetails?.loading && (
            <SlideoutFooterStandardButtons
              showCancel={false}
              showApply={true}
              applyText="Submit"
              doApply={submit}
              isApplyDisabled={!allGood}
              disabledText="Terms and conditions confirmation required"
            />
          )}
        </div>
      </div>
    );
  }

  function submit() {
    const { purchase_price, participation_purchase_price, pubSurveyCode, pub_survey_short_name } = details;
    const purchasePriceFormatted = purchase_price ? formatCurrency(purchase_price, 'USD') : undefined;
    const particpationPurchasePriceFormatted = participation_purchase_price
      ? formatCurrency(participation_purchase_price, 'USD')
      : undefined;

    props.actions.purchaseSubmit(
      purchasePriceFormatted,
      particpationPurchasePriceFormatted,
      pubSurveyCode,
      pub_survey_short_name,
      showParticipationPrice
    );
  }

  function closeAction() {
    props.actions.resetPurchaseSubmit();
    props.hideSlideout();
  }

  return (
    <div
      className={cn('marketplace-matches__purchase-slideout', { bigger: isBigger })}
      id="marketplace-matches__purchase-slideout"
    >
      <div className="slideout-header">
        <div className="survey-title">{`${details?.pub_survey_short_name} (${details?.pubSurveyCode})`}</div>

        <div className="return-link" onClick={onBack}>
          <i className="icon-reply" />
          Return to survey job details
        </div>

        <SlideoutHeaderButton
          buttonClass="slideout-header__button"
          iconClass="icon-cancel"
          onClick={props.hideSlideout}
          testId="marketplace-matches__navigation-close"
        />
      </div>

      {purchaseSubmit.loading ? (
        <ProgressStatusOverlay inProgress={purchaseSubmit.loading} primaryText="Submitting..." />
      ) : !purchaseSubmit.loading && purchaseSubmit.success ? (
        <SlideoutSubmitSuccess
          submitSuccessTitle="Successfully submitted request for this survey"
          submitSuccessDetails="You will receive an email confirmation shortly"
          actionText="Done"
          closeAction={closeAction}
          actionFunction={closeAction}
        />
      ) : (
        <Fragment>
          <SlideoutBody>
            {surveyDetails?.loading ? (
              <InPlaceIndicator />
            ) : (
              <FileViewer fileType={'pdf'} url={payscaleTermsPDFURL} errorComponent={noLegalDocsPlaceholder} />
            )}
          </SlideoutBody>

          <SlideoutFooter isBoxShadowTheme={true}>
            {getPayscaleTermCheck()}
            {getSurveyTermCheck()}
            {getSubmitRow()}
          </SlideoutFooter>
        </Fragment>
      )}
    </div>
  );
}

const mapStateToProps = ({ marketPricing }) => {
  return {
    ...marketPricing.MarketplaceMatchesReducer.marketplaceMatchesReducer
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...marketplaceMatchesActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseSlideout);
